import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Home, MapPin, TrendingUp, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import { trackEvent } from '@/components/Analytics';
import { SmsOptInCheckbox } from '@/components/SmsConsentDisclosure';

const API = process.env.REACT_APP_BACKEND_URL;

const HomeValuation = () => {
  const [step, setStep] = useState('address');  // address | details | submitted
  const [form, setForm] = useState({
    address: '', zip_code: '', beds: '', sqft: '', property_type: 'single-family',
    name: '', email: '', phone: '', timeframe: 'just curious', notes: '',
    sms_opt_in: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const goDetails = (e) => {
    e.preventDefault();
    if (!form.address.trim()) return toast.error('Enter your property address');
    setStep('details');
    trackEvent('home_valuation_step_1', { has_zip: !!form.zip_code });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error('Email is required to receive your report');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/home-valuation/request`, form);
      setResult(res.data);
      setStep('submitted');
      trackEvent('home_valuation_submit', { source: 'home_valuation_form' });
      if (window.fbq) window.fbq('track', 'Lead', { content_name: 'Home Valuation' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid="home-valuation-page">
      <SEO
        title="What's My Home Worth? Free Home Valuation | Bespoke Realty"
        description="Get a free, no-obligation home valuation for your Memphis, Southaven, Olive Branch, Hernando, or Collierville property. Local expertise, comparable sales analysis, and honest pricing."
        keywords="home valuation, what is my house worth, home value Memphis, CMA, home appraisal Mississippi, sell my home"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#434345] to-[#1a1a1a] text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-[#BCA480]/20 border border-[#BCA480]/40 rounded-full px-4 py-1.5 text-sm text-[#BCA480] mb-4">
            <Sparkles className="w-4 h-4" /> Free · Confidential · No obligation
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            What's My Home Worth?
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
            Get a personalized market analysis from a Bespoke Realty agent — grounded in real comparable sales, not automated guesses.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4">
          {/* Progress */}
          <div className="flex justify-center gap-2 mb-6">
            {['address', 'details', 'submitted'].map((s, i) => (
              <div key={s} className={`h-1.5 flex-1 max-w-24 rounded-full ${['address', 'details', 'submitted'].indexOf(step) >= i ? 'bg-[#BCA480]' : 'bg-gray-200'}`} />
            ))}
          </div>

          <Card>
            <CardContent className="p-6 md:p-8">
              {step === 'address' && (
                <form onSubmit={goDetails} className="space-y-4" data-testid="valuation-step-address">
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">Start with your address</h2>
                  <p className="text-sm text-gray-600 mb-4">Takes 60 seconds. Your info stays with our team — never sold.</p>
                  <div>
                    <Label>Property Address <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        className="pl-9"
                        value={form.address}
                        onChange={(e) => upd('address', e.target.value)}
                        placeholder="123 Main St, Olive Branch, MS"
                        required
                        autoFocus
                        data-testid="valuation-address"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>ZIP Code (helps us find comps)</Label>
                    <Input
                      value={form.zip_code}
                      onChange={(e) => upd('zip_code', e.target.value)}
                      placeholder="38654"
                      data-testid="valuation-zip"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#BCA480] hover:bg-[#a89470] text-white" data-testid="valuation-continue">
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              )}

              {step === 'details' && (
                <form onSubmit={submit} className="space-y-4" data-testid="valuation-step-details">
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">A few more details</h2>
                  <p className="text-sm text-gray-600 mb-4">For <strong>{form.address}</strong>. Adjust if needed.</p>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Bedrooms</Label>
                      <Input type="number" value={form.beds} onChange={(e) => upd('beds', e.target.value)} placeholder="4" data-testid="valuation-beds" />
                    </div>
                    <div>
                      <Label>Sqft (approx)</Label>
                      <Input type="number" value={form.sqft} onChange={(e) => upd('sqft', e.target.value)} placeholder="2200" data-testid="valuation-sqft" />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <select
                        className="w-full h-10 border border-gray-200 rounded px-2 text-sm"
                        value={form.property_type}
                        onChange={(e) => upd('property_type', e.target.value)}
                        data-testid="valuation-type"
                      >
                        <option value="single-family">Single Family</option>
                        <option value="condo">Condo</option>
                        <option value="townhome">Townhome</option>
                        <option value="multi-family">Multi-family</option>
                        <option value="land">Land / Lot</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label>When are you thinking of selling?</Label>
                    <select
                      className="w-full h-10 border border-gray-200 rounded px-3 text-sm"
                      value={form.timeframe}
                      onChange={(e) => upd('timeframe', e.target.value)}
                      data-testid="valuation-timeframe"
                    >
                      <option value="just curious">Just curious</option>
                      <option value="0-3 months">In the next 3 months</option>
                      <option value="3-6 months">3–6 months</option>
                      <option value="6-12 months">6–12 months</option>
                      <option value="1+ years">More than a year</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Your Name</Label>
                      <Input value={form.name} onChange={(e) => upd('name', e.target.value)} data-testid="valuation-name" />
                    </div>
                    <div>
                      <Label>Phone (optional)</Label>
                      <Input value={form.phone} onChange={(e) => upd('phone', e.target.value)} placeholder="(901) 555-0000" data-testid="valuation-phone" />
                      <SmsOptInCheckbox
                        checked={form.sms_opt_in}
                        onChange={(v) => upd('sms_opt_in', v)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Email <span className="text-red-500">*</span></Label>
                    <Input type="email" value={form.email} onChange={(e) => upd('email', e.target.value)} required data-testid="valuation-email" />
                  </div>
                  <div>
                    <Label>Anything special about your home? (optional)</Label>
                    <Textarea rows={2} value={form.notes} onChange={(e) => upd('notes', e.target.value)} placeholder="Recent renovations, upgrades, unique features…" data-testid="valuation-notes" />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setStep('address')} disabled={submitting}>Back</Button>
                    <Button type="submit" className="flex-1 bg-[#BCA480] hover:bg-[#a89470] text-white" disabled={submitting} data-testid="valuation-submit">
                      {submitting ? 'Sending…' : 'Get My Valuation'}
                    </Button>
                  </div>
                </form>
              )}

              {step === 'submitted' && (
                <div className="text-center py-6" data-testid="valuation-submitted">
                  <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Your request is in</h2>
                  <p className="text-gray-600">A Bespoke Realty agent will send your full comparative market analysis within <strong>24 hours</strong>.</p>

                  {result?.estimate_low && (
                    <div className="mt-6 bg-[#faf8f4] border-2 border-dashed border-[#BCA480] rounded-lg p-6">
                      <div className="text-xs text-gray-600 uppercase tracking-wider">Preliminary estimate range</div>
                      <div className="text-3xl md:text-4xl font-bold text-[#434345] my-2">
                        ${result.estimate_low.toLocaleString()} – ${result.estimate_high.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500 max-w-md mx-auto">
                        Rough range from recent active listings in your ZIP. Your agent's full CMA will be much more accurate — factoring condition, upgrades, and recent sold comps.
                      </div>
                    </div>
                  )}

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
                    <div className="bg-gray-50 p-4 rounded flex items-start gap-2">
                      <TrendingUp className="w-5 h-5 text-[#BCA480] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-sm">Real comps</div>
                        <div className="text-xs text-gray-600">Sold prices from your neighborhood, not just Zestimates.</div>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded flex items-start gap-2">
                      <Home className="w-5 h-5 text-[#BCA480] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-sm">Local expertise</div>
                        <div className="text-xs text-gray-600">TN/MS agents who know your specific neighborhood.</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default HomeValuation;
